import React, { useState } from 'react';
import { Modal, NumberInput } from '@mantine/core';
import { Button } from '../../../../components/Button';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import type { TenantView } from '../../../../services/tenants/tenants.service';
import classes from './vat-edit-modal.module.scss';

type EditVatRateModalProps = {
  tenant: TenantView | null;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (tenant: TenantView, vatRate: number) => void;
};

export const EditVatRateModal: React.FC<EditVatRateModalProps> = ({
  tenant,
  isSaving,
  onClose,
  onSubmit,
}) => {
  const [vatRate, setVatRate] = useState<number | string>(tenant?.vatRate ?? 0);

  React.useEffect(() => {
    if (tenant) setVatRate(tenant.vatRate);
  }, [tenant]);

  if (!tenant) return null;

  // Treat a cleared input ('') as invalid — Number('') is 0, which would silently submit 0% VAT.
  const numericValue =
    typeof vatRate === 'number' ? vatRate : String(vatRate).trim() === '' ? NaN : Number(vatRate);
  const isValid = Number.isFinite(numericValue) && numericValue >= 0 && numericValue <= 100;

  return (
    <Modal
      opened={tenant != null}
      onClose={onClose}
      onContextMenu={(event) => event.preventDefault()}
      title={`עדכון מע"מ — ${tenant.companyName}`}
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
      withinPortal
      closeOnClickOutside={false}
      trapFocus={false}
      closeButtonProps={{ 'data-testid': 'vat-edit-close' } as Record<string, string>}
    >
      <NumberInput
        data-testid="vat-edit-input"
        label='שיעור מע"מ (%)'
        value={vatRate}
        onChange={setVatRate}
        min={0}
        max={100}
        step={1}
        decimalScale={2}
        size="md"
        radius="md"
      />
      <div className={classes.modalActions}>
        <Button variant="cancel" onClick={onClose} disabled={isSaving} dataTestId="vat-edit-cancel">
          ביטול
        </Button>
        <Button
          variant="save"
          onClick={() => onSubmit(tenant, numericValue)}
          disabled={!isValid || isSaving}
          loading={isSaving}
          loadingLabel="שומר..."
          dataTestId="vat-edit-save"
        >
          שמור
        </Button>
      </div>
    </Modal>
  );
};
